import {
  SearchControllerSearchRequest,
  TrackSearchDto,
} from '@cratehackers/api-client';
import { AgentAttachedTrack } from '../components/agentChat/agentAttachedTrack';
import { formatMusicalKey } from '../utils/formatMusicalKey';
import ApiService from './Api.service';

type SearchKeys = {
  Camelot?: string;
  camelot?: string;
};

const readAlbumPicture = (album: unknown): string => {
  if (album && typeof album === 'object' && 'picture' in album) {
    const picture = (album as { picture?: unknown }).picture;
    return typeof picture === 'string' ? picture : '';
  }
  return '';
};

const readKeyCandidate = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const camelot = record.Camelot ?? record.camelot;
    if (typeof camelot === 'string') return camelot;
  }
  return '';
};

const readSearchCamelotKey = (item: TrackSearchDto): string => {
  const keys = item.keys as
    | SearchKeys
    | Record<string, unknown>
    | null
    | undefined;
  const raw =
    readKeyCandidate(keys?.Camelot ?? keys?.camelot) ||
    readKeyCandidate(item.key) ||
    readKeyCandidate(keys);
  return formatMusicalKey(raw) || '';
};

const readBpm = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.round(parsed);
  }
  return 0;
};

const readArtistName = (artist: unknown): string => {
  if (typeof artist === 'string') return artist.trim();
  if (artist && typeof artist === 'object' && 'name' in artist) {
    const name = (artist as { name?: unknown }).name;
    return typeof name === 'string' ? name.trim() : '';
  }
  return '';
};

const mapSearchItem = (
  item: TrackSearchDto,
  index: number,
): AgentAttachedTrack | null => {
  try {
    return {
      id: String(item.uuid || item.id || `search-${index}`),
      title: typeof item.title === 'string' ? item.title : '',
      artist: readArtistName(item.artist),
      albumCover: readAlbumPicture(item.album),
      bpm: readBpm(item.bpm),
      key: readSearchCamelotKey(item),
    };
  } catch (error) {
    console.warn('[AgentTrackSearch] skipped malformed search row:', error);
    return null;
  }
};

export const searchCatalogTracks = async (
  term: string,
  limit = 8,
): Promise<AgentAttachedTrack[]> => {
  const trimmed = term.trim();
  if (trimmed.length < 3) return [];

  try {
    const api = ApiService.getApiClient();
    const req: SearchControllerSearchRequest = {
      searchDto: {
        term: trimmed,
        objectType: 'track',
        offset: 0,
        additionalParams: { markets: [] },
        raw: false,
        limit,
      },
    };

    const resp = await api.searchControllerSearch(req);
    if (!resp?.items?.length) return [];

    return resp.items
      .map(mapSearchItem)
      .filter((track): track is AgentAttachedTrack => track !== null);
  } catch (error) {
    console.error('[AgentTrackSearch] search failed:', error);
    return [];
  }
};
